// Phase 3.1c — Attachment drawer.
//
// Replaces the antd <Drawer> + antd <Upload>/<Dragger> attachment manager with a
// shadcn <Sheet> + a FileDropzone-style picker (drag/drop + click) and a preview
// gallery. Presentation only — the parent still owns imgList/videoList/singleImg
// and the effect-driven AWS upload pipeline (uploadPhotoToAWS → imageUploadApi →
// createOrUpdateAttachmentAction), so the upload/save/delete/cover behaviour is
// unchanged. Each picked file is handed back verbatim to the parent's pick
// handler, which runs the legacy customRequest body (extension + count guard).
//
// Note: the shared <FileDropzone> is a single-file/single-preview widget; the
// attachment manager needs multi-file adds (up to 5) plus a separate persistent
// gallery, so we render the same FileDropzone visual language inline rather than
// embedding that component.
import * as React from "react";
import { UploadCloud, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/sheet";
import { Button } from "ui/button";
import { cn } from "lib/utils";
import { isArrayNotEmpty } from "../../Utils/helpers";

const IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}products/images/`;
const VIDEO_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}products/videos/`;

export interface AttachmentSheetProps {
  open: boolean;
  onClose: () => void;
  // toggle state for the two drop areas (legacy showAttachment / showVideoAttachment)
  showImage: boolean;
  setShowImage: (v: boolean) => void;
  showVideo: boolean;
  setShowVideo: (v: boolean) => void;
  // local (pre/post-upload preview) + server-side attachment lists
  imgList: any[];
  videoList: any[];
  attachmentImages: any[];
  attachmentVideos: any[];
  // cover-image selection
  addCoverImage: boolean;
  setAddCoverImage: (v: boolean) => void;
  imgFileUuid: any;
  // parent-owned handlers (legacy customRequest / setImages / setVideos / coverImage)
  onPickImage: (file: File) => void;
  onPickVideo: (file: File) => void;
  onRemoveImage: (data: any, value: number) => void;
  onRemoveVideo: (data: any, value: number) => void;
  onSetCoverImage: (data: any, fileUuid: any) => void;
}

interface DropAreaProps {
  accept: string;
  onFiles: (files: File[]) => void;
}

function DropArea({ accept, onFiles }: DropAreaProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);

  const emit = (list: FileList | null) => {
    if (!list) return;
    onFiles(Array.from(list));
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); emit(e.dataTransfer.files); }}
      className={cn(
        "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center transition-colors outline-none",
        "hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring",
        dragging ? "border-primary bg-primary/5" : "border-border"
      )}
    >
      <UploadCloud className="h-6 w-6 text-muted-foreground" />
      <span className="text-sm font-medium text-foreground">Drag &amp; drop files or click to upload</span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => { emit(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

export function AttachmentSheet({
  open,
  onClose,
  showImage,
  setShowImage,
  showVideo,
  setShowVideo,
  imgList,
  videoList,
  attachmentImages,
  attachmentVideos,
  addCoverImage,
  setAddCoverImage,
  imgFileUuid,
  onPickImage,
  onPickVideo,
  onRemoveImage,
  onRemoveVideo,
  onSetCoverImage,
}: AttachmentSheetProps) {
  const hasImages = isArrayNotEmpty(imgList) || (attachmentImages?.length ?? 0) > 0;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Attachment</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-6 mz">
          {/* ---------- Images ---------- */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Image</span>
              <Button variant="outline" size="sm" onClick={() => setShowImage(!showImage)}>
                + Upload
              </Button>
            </div>

            {showImage && (
              <DropArea
                accept=".png,.jpg,.jpeg"
                onFiles={(files) => files.forEach((f) => onPickImage(f))}
              />
            )}

            {hasImages && (
              <div className="flex justify-end">
                <Button variant="link" className="h-auto p-0" onClick={() => setAddCoverImage(!addCoverImage)}>
                  {addCoverImage ? "Cancel" : "Add Cover Image"}
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              {isArrayNotEmpty(imgList)
                ? imgList.map((item: any, index: number) => (
                    <ImageTile
                      key={`local-${index}`}
                      src={item?.previewImage}
                      isCover={item?.fileUuid === imgFileUuid && addCoverImage}
                      onSelect={() => onSetCoverImage(item, item?.fileUuid)}
                      showRemove={!addCoverImage}
                      onRemove={() => onRemoveImage(item, 1)}
                    />
                  ))
                : null}
              {isArrayNotEmpty(attachmentImages)
                ? attachmentImages.map((item: any, index: number) => (
                    <ImageTile
                      key={`server-${index}`}
                      src={`${IMG_BASE}${item?.fileUuid}`}
                      isCover={item?.fileUuid === imgFileUuid && addCoverImage}
                      onSelect={() => onSetCoverImage(item, item?.fileUuid)}
                      showRemove={!addCoverImage}
                      onRemove={() => onRemoveImage(item, 0)}
                    />
                  ))
                : null}
            </div>
          </section>

          {/* ---------- Videos ---------- */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Video</span>
              <Button variant="outline" size="sm" onClick={() => setShowVideo(!showVideo)}>
                + Upload
              </Button>
            </div>

            {showVideo && (
              <DropArea
                accept=".mp4,.mpeg,.webm"
                onFiles={(files) => files.forEach((f) => onPickVideo(f))}
              />
            )}

            <div className="flex flex-wrap gap-3">
              {isArrayNotEmpty(videoList)
                ? videoList.map((item: any, index: number) => (
                    <VideoTile key={`local-${index}`} src={item?.previewImage} onRemove={() => onRemoveVideo(item, 1)} />
                  ))
                : null}
              {isArrayNotEmpty(attachmentVideos)
                ? attachmentVideos.map((item: any, index: number) => (
                    <VideoTile key={`server-${index}`} src={`${VIDEO_BASE}${item?.fileUuid}`} onRemove={() => onRemoveVideo(item, 0)} />
                  ))
                : null}
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface ImageTileProps {
  src: string;
  isCover: boolean;
  onSelect: () => void;
  showRemove: boolean;
  onRemove: () => void;
}

function ImageTile({ src, isCover, onSelect, showRemove, onRemove }: ImageTileProps) {
  return (
    <div className={cn("relative h-24 w-24 overflow-hidden rounded-lg border", isCover && "ring-2 ring-primary")}>
      {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
      <img src={src} alt="attachment" className="h-full w-full cursor-pointer object-cover" onClick={onSelect} />
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove image"
          className="absolute right-1 top-1 rounded-md bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function VideoTile({ src, onRemove }: { src: string; onRemove: () => void }) {
  return (
    <div className="relative h-24 w-32 overflow-hidden rounded-lg border">
      <video src={src} className="h-full w-full object-cover" controls />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove video"
        className="absolute right-1 top-1 rounded-md bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default AttachmentSheet;
